"use client";

import * as React from "react";
import { useContext } from "react";

import { cn } from "lib/utils";
import { Card } from "components/ui/card";
import { X } from "lucide-react";
import { IntlContext } from "context";

import { RegisterStep, useRegisterStepper } from "./RegisterStepperProvider";
import { Avatar, AvatarImage, AvatarFallback } from "components/ui/avatar";

export type RegisterStepperHero = {
    imageSrc: string;
    brandText?: string;
};

export type RegisterStepperLayoutProps = {
    title: string;
    steps: RegisterStep[];
    type?: "user" | "provider";
    showOnConfirmation?: boolean;
    variant?: "simple" | "split";
    hero?: RegisterStepperHero;
    className?: string;
    onClose?: () => void;
    children: React.ReactNode;
};

function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}

function RegisterStepSegments({ total, activeIndex }: { total: number; activeIndex: number }) {
    const n = Math.max(total, 1);
    const clampedIndex = clamp(activeIndex, 0, n - 1);

    return (
        <div
            className="flex h-[7px] w-full min-w-[10.5rem] max-w-[13rem] items-center gap-[5px]"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={n}
            aria-valuenow={clampedIndex + 1}
            aria-label={`Step ${clampedIndex + 1} of ${n}`}
        >
            {Array.from({ length: n }, (_, i) => {
                const isCurrent = i === clampedIndex;
                const filled = i <= clampedIndex;
                return (
                    <div
                        key={i}
                        className={cn(
                            "h-full min-w-[14px] rounded-full transition-all duration-300 ease-out",
                            isCurrent ? "flex-[2.6] basis-0" : "flex-1 basis-0",
                            filled ? "bg-primary" : "bg-muted"
                        )}
                    />
                );
            })}
        </div>
    );
}

export function RegisterStepperLayout({
    steps,
    type = "provider",
    showOnConfirmation = false,
    variant = "split",
    hero = {
        imageSrc: "/images/bg-register.png",
        brandText: "Twoghether",
    },
    className,
    onClose,
    children,
}: RegisterStepperLayoutProps) {
    const intl = useContext(IntlContext);
    const { activeIndex } = useRegisterStepper();
    const total = Math.max(steps.length, 1);
    const current = clamp(activeIndex + 1, 1, total);
    const i18n = "authentication.register";
    const stepOfTotal = intl.formatMessage(`${i18n}.stepper.step_of_total`, {
        current,
        total,
    });
    const stepIndex = intl.formatMessage(`${i18n}.stepper.step_index`, { current });
    const closeLabel = intl.formatMessage(`${i18n}.stepper.close`);
    const activeStepLabel = steps[activeIndex]?.label ?? "";
    const activeStepTitle = steps[activeIndex]?.title ?? "";
    const activeStepDescription = steps[activeIndex]?.description ?? "";
    const activeStepFootnote = steps[activeIndex]?.footnote ?? false;
    const isConfirmation = steps[activeIndex]?.key === "confirmation";

    const CLIENT_AVATARS = [
        { src: "https://i.pravatar.cc/150?img=13", fallback: "AB" },
        { src: "https://i.pravatar.cc/150?img=48", fallback: "CD" },
        { src: "https://i.pravatar.cc/150?img=32", fallback: "EF" },
        { src: "https://i.pravatar.cc/150?img=24", fallback: "GH" },
    ] as const;

    const PROVIDER_AVATARS = [
        { src: "https://i.pravatar.cc/150?img=17", fallback: "AB" },
        { src: "https://i.pravatar.cc/150?img=50", fallback: "CD" },
        { src: "https://i.pravatar.cc/150?img=34", fallback: "EF" },
        { src: "https://i.pravatar.cc/150?img=26", fallback: "GH" },
    ] as const;
    const FOOTNOTE_AVATARS = type === "provider" ? CLIENT_AVATARS : PROVIDER_AVATARS;

    if (isConfirmation && !showOnConfirmation) {
        return <>{children}</>;
    }

    if (variant === "simple") {
        return (
            <Card className={cn("mx-auto w-full max-w-3xl", className)}>
                <div className="space-y-3 p-6">
                    <div className="flex items-center justify-between gap-4">
                        <div className="text-sm font-semibold tracking-tight">{activeStepLabel}</div>
                        <div className="text-xs font-medium text-muted-foreground">
                            {stepOfTotal}
                        </div>
                    </div>

                    <RegisterStepSegments total={total} activeIndex={activeIndex} />
                </div>

                <div className="px-6 pb-8">{children}</div>
            </Card>
        );
    }

    return (
        <Card className={cn("mx-auto w-full h-full overflow-hidden", className)}>
            <div className="grid h-full grid-cols-1 lg:grid-cols-12">
                <aside className="relative hidden overflow-hidden lg:col-span-5 lg:block">
                    <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${hero.imageSrc})` }}
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,15,44,0.90)_0%,rgba(10,15,44,0.60)_50%,rgba(10,15,44,1)_100%)]" />

                    <div className="relative flex h-[680px] flex-col px-12 py-12 text-white">
                        <img src="/images/logo/logo2025-secondary_hover.svg" className="w-40" />

                        <div className="mt-16 space-y-4">
                            <h2 className="text-4xl font-black leading-tight">{activeStepTitle}</h2>
                            {activeStepDescription ? (
                                <p className="max-w-sm text-lg text-white/80">{activeStepDescription}</p>
                            ) : null}
                        </div>

                        {activeStepFootnote ? (
                            <div className="mt-auto rounded-3xl border border-white/10 bg-white/10 px-6 py-5 text-sm text-white/90 backdrop-blur">
                                <div className="flex items-center gap-12">
                                    <div className="flex -space-x-2">
                                        {FOOTNOTE_AVATARS.map(({ src, fallback }) => (
                                            <Avatar key={fallback} className="h-9 w-9 border-2 border-slate-900">
                                                <AvatarImage src={src} />
                                                <AvatarFallback className="bg-muted text-xs text-muted-foreground">
                                                    {fallback}
                                                </AvatarFallback>
                                            </Avatar>
                                        ))}
                                    </div>
                                    <div className="text-right text-white">
                                        <p className="text-sm font-bold tracking-wide">
                                            {type === "provider" ? intl.formatMessage(`${i18n}.provider_flow.footnote`) : intl.formatMessage(`${i18n}.client_flow.footnote`)}
                                        </p>
                                    </div>
                                </div>
                                <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-white/70">
                                    {intl.formatMessage(`${i18n}.provider_flow.footnote.bottom`)}
                                </p>
                            </div>
                        ) : null}
                    </div>
                </aside>

                <section className="bg-background lg:col-span-7">
                    <div className="flex h-full flex-col">
                        <header className="border-b border-border/70 px-8 py-5">
                            <div className="flex items-start justify-between gap-6">
                                <div className="flex items-start gap-8">
                                    <div className="space-y-2">
                                        <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground/70">
                                            {stepOfTotal}
                                        </div>
                                        <RegisterStepSegments total={total} activeIndex={activeIndex} />
                                    </div>

                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-wider text-primary">
                                            {stepIndex}
                                        </div>
                                        <div className="text-base font-black tracking-tight">
                                            {activeStepLabel}
                                        </div>
                                    </div>
                                </div>

                                {onClose ? (
                                    <button
                                        type="button"
                                        className="rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                                        aria-label={closeLabel}
                                        onClick={onClose}
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                ) : null}
                            </div>
                        </header>

                        <div className="flex-1 px-8 py-10 lg:px-10 lg:py-12">
                            <div className="mx-auto h-full w-full max-w-[640px]">
                                {children}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </Card>
    );
}
